import { Controller } from "@hotwired/stimulus"

/**
 * Manages stacking sliding panels (50% width, full height) for previewing
 * whatever a link points at. Panels stack when links inside a preview are
 * clicked, creating a browsable depth of references.
 *
 * - Clicking a reference opens a new panel on top of any existing ones
 * - Each stacked panel shifts slightly left to show depth
 * - Close button / Escape removes only the topmost panel
 * - Backdrop click closes all panels
 *
 * Despite the name this is content-agnostic: it fetches `data-preview-url` and
 * injects the response. Law articles were the first caller; Gobierto
 * Conocimiento topics are the second. Each trigger names its own panel via
 * `data-preview-label`, so the badge says what the reader is looking at.
 *
 * @target backdrop   - Semi-transparent backdrop behind all panels
 * @target container  - Wrapper where dynamically created panels are appended
 */
export default class extends Controller {
  static targets = ["backdrop", "container"]

  connect() {
    this._stack = []  // Array of { url, element, abortController }
    this._onKeyDown = this._handleKeyDown.bind(this)
    this._onBeforeCache = this._reset.bind(this)
    document.addEventListener("turbo:before-cache", this._onBeforeCache)
    this._reset()
  }

  disconnect() {
    this._reset()
    document.removeEventListener("turbo:before-cache", this._onBeforeCache)
  }

  open(event) {
    // A trigger can be the reference link itself, not just a pill beside it, so
    // the browser's own gestures have to survive: middle-click, cmd/ctrl-click
    // and shift-click still open the target the way the reader asked for.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
    if (typeof event.button === "number" && event.button !== 0) return

    event.preventDefault()
    event.stopPropagation()
    const link = event.currentTarget
    const previewUrl = link.dataset.previewUrl
    if (!previewUrl) return

    // If this article is already the topmost panel, close it (toggle)
    const top = this._stack[this._stack.length - 1]
    if (top && top.url === previewUrl) {
      this._closeTop()
      return
    }

    // If already in the stack (not top), flash it — don't open a duplicate
    const existing = this._stack.find(e => e.url === previewUrl)
    if (existing) {
      existing.element.classList.add("ring-2", "ring-blue-300")
      setTimeout(() => existing.element.classList.remove("ring-2", "ring-blue-300"), 600)
      return
    }

    this._pushPanel(previewUrl, link.dataset.previewLabel)
  }

  close() {
    this._closeTop()
  }

  closeAll() {
    while (this._stack.length > 0) {
      const entry = this._stack.pop()
      entry.abortController?.abort()
      entry.element.remove()
    }
    this._hideBackdrop()
    document.removeEventListener("keydown", this._onKeyDown)
  }

  _reset() {
    (this._stack || []).forEach(entry => {
      try { entry.abortController?.abort() } catch (_) { /* already aborted */ }
      entry.element.remove()
    })
    this._stack = []
    if (this.hasContainerTarget) this.containerTarget.replaceChildren()
    if (this.hasBackdropTarget) this.backdropTarget.classList.add("hidden", "opacity-0")
    document.removeEventListener("keydown", this._onKeyDown)
  }

  _pushPanel(url, label) {
    const depth = this._stack.length
    const panel = this._createPanelElement(depth, label)

    this.containerTarget.appendChild(panel)
    this._showBackdrop()

    const abortController = new AbortController()
    const entry = { url, element: panel, abortController }
    this._stack.push(entry)

    // Click on an obscured panel's visible strip to navigate back to it
    panel.addEventListener("click", (e) => {
      const idx = this._stack.indexOf(entry)
      if (idx >= 0 && idx < this._stack.length - 1) {
        e.stopPropagation()
        this._closeAbove(idx)
      }
    })

    // Slide in
    requestAnimationFrame(() => {
      panel.classList.remove("translate-x-full")
    })

    document.addEventListener("keydown", this._onKeyDown)
    this._loadContent(entry)
  }

  _closeAbove(index) {
    while (this._stack.length > index + 1) {
      this._closeTop()
    }
  }

  _closeTop() {
    if (this._stack.length === 0) return

    const entry = this._stack.pop()
    try { entry.abortController?.abort() } catch (_) { /* already aborted */ }

    // Slide out
    entry.element.classList.add("translate-x-full")
    setTimeout(() => entry.element.remove(), 200)

    if (this._stack.length === 0) {
      this._hideBackdrop()
      document.removeEventListener("keydown", this._onKeyDown)
    }
  }

  _showBackdrop() {
    this.backdropTarget.classList.remove("hidden")
    requestAnimationFrame(() => {
      this.backdropTarget.classList.remove("opacity-0")
    })
  }

  _hideBackdrop() {
    this.backdropTarget.classList.add("opacity-0")
    setTimeout(() => this.backdropTarget.classList.add("hidden"), 200)
  }

  async _loadContent(entry) {
    const body = entry.element.querySelector("[data-preview-body]")

    try {
      const res = await fetch(entry.url, {
        headers: { "Accept": "text/html", "X-Requested-With": "XMLHttpRequest" },
        signal: entry.abortController.signal
      })

      if (!res.ok) {
        body.innerHTML = '<p class="text-gray-400 text-sm py-8 text-center">No se pudo cargar el artículo</p>'
        return
      }

      body.innerHTML = await res.text()
    } catch (e) {
      if (e.name !== "AbortError") {
        body.innerHTML = '<p class="text-gray-400 text-sm py-8 text-center">Error al cargar</p>'
      }
    }
  }

  _createPanelElement(depth, label) {
    const shrink = depth * 24  // each panel is 24px narrower than the previous

    const panel = document.createElement("div")
    panel.className = [
      "fixed top-0 right-0 bottom-0 z-50",
      "translate-x-full transition-transform duration-200 ease-out",
      "bg-white shadow-2xl border-l border-gray-200 flex flex-col"
    ].join(" ")
    panel.style.zIndex = 50 + depth
    // The sidebar's CSS transform turns it into the containing block for
    // position:fixed descendants AND clips anything that overflows it
    // (overflow:hidden on .global-sidebar-panel). A viewport-relative width
    // would extend past the sidebar and get clipped, so when inside the
    // sidebar we widen to 90% of the sidebar instead of the default 50%.
    const insideSidebar = this.containerTarget.closest("#global-sidebar-layout")
    const widthPercent = insideSidebar ? 90 : 50
    panel.style.width = `calc(${widthPercent}% - ${shrink}px)`

    // Header
    const header = document.createElement("div")
    header.className = "flex items-center justify-between px-6 py-3 border-b border-gray-200 bg-gray-50 shrink-0"

    const title = document.createElement("div")
    title.className = "flex items-center gap-2"

    const badge = document.createElement("span")
    badge.className = "text-xs uppercase tracking-wide font-bold"
    badge.style.color = "var(--color-brown, #6b5b4e)"
    badge.textContent = label || "Referencia"

    if (depth > 0) {
      const depthBadge = document.createElement("span")
      depthBadge.className = "text-xs text-gray-400 tabular-nums"
      depthBadge.textContent = `(${depth + 1})`
      title.appendChild(badge)
      title.appendChild(depthBadge)
    } else {
      title.appendChild(badge)
    }

    const closeBtn = document.createElement("button")
    closeBtn.className = "text-gray-400 hover:text-gray-600 transition p-1 -mr-1"
    closeBtn.setAttribute("aria-label", I18n.article_preview.close_label)
    closeBtn.setAttribute("data-action", "click->legislation--article-preview#close")
    closeBtn.innerHTML = '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M6 18L18 6M6 6l12 12"/></svg>'

    header.appendChild(title)
    header.appendChild(closeBtn)

    // Body with loading skeleton
    const body = document.createElement("div")
    body.setAttribute("data-preview-body", "")
    body.className = "flex-1 overflow-y-auto px-6 py-6"
    body.innerHTML = this._loadingSkeleton()

    panel.appendChild(header)
    panel.appendChild(body)
    return panel
  }

  _handleKeyDown(event) {
    if (event.key === "Escape") this._closeTop()
  }

  _loadingSkeleton() {
    return [
      '<div class="animate-pulse space-y-4">',
      '<div class="h-5 bg-gray-200 rounded w-2/3"></div>',
      '<div class="h-3 bg-gray-100 rounded w-1/3"></div>',
      '<div class="space-y-2 mt-6">',
      '<div class="h-3 bg-gray-100 rounded w-full"></div>',
      '<div class="h-3 bg-gray-100 rounded w-full"></div>',
      '<div class="h-3 bg-gray-100 rounded w-5/6"></div>',
      '<div class="h-3 bg-gray-100 rounded w-full"></div>',
      '<div class="h-3 bg-gray-100 rounded w-4/6"></div>',
      "</div>",
      "</div>"
    ].join("")
  }
}
